import type {
  ActivityFeedResponse,
  ApiResponse,
  AuthResponse,
  BankAccount,
  BankAccountFormData,
  Category,
  Client,
  ClientFormData,
  CustomerAnalytics,
  CreateCategoryData,
  CreateProductData,
  CreateProductVariantData,
  CreateUserRequest,
  DashboardStats,
  HeroSlide,
  HeroSlideFormData,
  LoginCredentials,
  Order,
  OrderStats,
  Product,
  ProductImage,
  ProductVariant,
  QueryParams,
  Review,
  ReviewFormData,
  SalesData,
  Testimonial,
  TestimonialFormData,
  TopProduct,
  UpdateOrderStatusData,
  User,
  Voucher,
  VoucherFormData,
  VoucherUsageStatistics,
} from '@/types/api'

// Import RecentOrder from the component file since it's specific to that component
type RecentOrder = {
  id: string
  order_number: string
  customer: {
    name: string
    email: string
  }
  total_amount: number
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded'
  payment_status: string
  created_at: string
  status_display: string
}

class ApiClient {
  private baseURL: string
  private readonly ACCESS_TOKEN_KEY = 'admin_access_token'
  private readonly REFRESH_TOKEN_KEY = 'admin_refresh_token'
  private readonly TOKEN_EXPIRY_KEY = 'admin_token_expiry'

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || '') {
    // In development, use empty string to leverage Vite proxy
    // In production, use the full API URL
    this.baseURL = baseURL || (import.meta.env.DEV ? '' : 'http://localhost:8000')
  }

  public getBaseURL(): string {
    return this.baseURL || 'http://localhost:8000'
  }

  private buildQuery(params: Record<string, unknown> = {}): string {
    const usp = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) {
        value.forEach((v) => usp.append(key, String(v)))
      } else {
        usp.set(key, String(value))
      }
    })
    const str = usp.toString()
    return str.length ? `?${str}` : ''
  }

  private getAccessToken(): string | null {
    try {
      return localStorage.getItem(this.ACCESS_TOKEN_KEY)
    } catch {
      return null
    }
  }

  private getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY)
    } catch {
      return null
    }
  }

  private saveTokens(accessToken: string, refreshToken: string, expiresAt: string): void {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken)
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken)
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt)
    } catch {
      void 0
    }
  }

  private clearTokens() {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY)
      localStorage.removeItem(this.REFRESH_TOKEN_KEY)
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY)
    } catch {
      void 0
    }
  }

  private async refreshTokens(): Promise<boolean> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) return false

    try {
      const refreshUrl = this.baseURL
        ? `${this.baseURL}/api/v1/admin/auth/refresh`
        : `/api/v1/admin/auth/refresh`
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (!response.ok) {
        this.clearTokens()
        return false
      }

      const data = await response.json()
      if (data.success && data.data) {
        this.saveTokens(data.data.access_token, data.data.refresh_token, data.data.expires_at)
        return true
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      this.clearTokens()
    }

    return false
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    // Use relative URL in development (Vite proxy) or full URL in production
    const url = this.baseURL ? `${this.baseURL}/api/v1${endpoint}` : `/api/v1${endpoint}`
    let accessToken = this.getAccessToken()

    const makeRequest = async (token: string | null): Promise<Response> => {
      const baseHeaders: Record<string, string> = {
        Accept: 'application/json',
      }

      // Only set Content-Type for non-FormData requests when body exists
      if (options.body && !(options.body instanceof FormData)) {
        baseHeaders['Content-Type'] = 'application/json'
      }

      if (token) {
        baseHeaders['Authorization'] = `Bearer ${token}`
      }

      const normalizeHeaders = (h?: HeadersInit): Record<string, string> => {
        const out: Record<string, string> = {}
        if (!h) return out
        if (h instanceof Headers) {
          h.forEach((value, key) => {
            out[key] = value
          })
          return out
        }
        if (Array.isArray(h)) {
          h.forEach(([key, value]) => {
            out[key] = value
          })
          return out
        }
        if (typeof h === 'object') {
          Object.entries(h).forEach(([key, value]) => {
            out[key] = String(value)
          })
        }
        return out
      }

      const finalHeaders: Record<string, string> = {
        ...baseHeaders,
        ...normalizeHeaders(options.headers),
      }

      const config: RequestInit = {
        headers: finalHeaders,
        body: options.body,
        method: options.method,
        credentials: 'omit',
      }

      return fetch(url, config)
    }

    try {
      let response = await makeRequest(accessToken)

      // If we get a 401 and have a refresh token, try to refresh
      if (!response.ok && response.status === 401 && this.getRefreshToken()) {
        const refreshed = await this.refreshTokens()
        if (!refreshed) {
          this.clearTokens()
          // Only redirect if we're not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        } else {
          // Retry with new access token
          accessToken = this.getAccessToken()
          response = await makeRequest(accessToken)
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const jsonData = await response.json()

      // Return full response as-is from backend
      // Backend returns: { data: {...}, message?: "...", meta?: {...} }
      // Keep consistent structure - don't extract data field
      return {
        ...jsonData,
        status: response.status,
      }
    } catch (error) {
      // Handle network errors (failed to fetch, CORS, etc.)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const errorMessage = `Cannot connect to server. Please check if the backend is running at ${this.baseURL}`
        console.error('Network error:', errorMessage, error)
        throw new Error(errorMessage)
      }

      // Handle other errors
      if (error instanceof Error) {
        // If it's already a meaningful error message, use it
        if (error.message && error.message !== 'API request failed') {
          throw error
        }
      }

      throw new Error(error instanceof Error ? error.message : 'API request failed')
    }
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
      headers: {},
    })

    // Save tokens based on response structure
    // Backend returns: { data: { user, access_token, refresh_token, token_type, expires_at } }
    if (response.data?.access_token) {
      this.saveTokens(
        response.data.access_token,
        response.data.refresh_token,
        response.data.expires_at
      )
    }

    return response
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.request<void>('/admin/auth/logout', {
      method: 'POST',
    })

    this.clearTokens()
    return response
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.request<{
      id: string
      name: string
      email: string
      phone?: string
      role: string
      email_verified_at?: string
      created_at: string
      updated_at: string
      permissions?: unknown
    }>('/admin/auth/me')

    // Map backend response (name) to frontend User type (full_name)
    return {
      ...response,
      data: {
        id: response.data.id,
        email: response.data.email,
        full_name: response.data.name,
        phone: response.data.phone,
        role: response.data.role as 'customer' | 'admin',
        email_verified_at: response.data.email_verified_at,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
      } as User,
    }
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    // Get current user ID from the user data
    const currentUserResponse = await this.getCurrentUser()
    const userId = currentUserResponse.data?.id

    if (!userId) {
      throw new Error('User ID not found')
    }

    return this.request<User>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async resetPassword(email: string): Promise<ApiResponse<void>> {
    return this.request<void>('/admin/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  // Dashboard endpoints
  async getDashboardStats(
    params: Omit<QueryParams, 'period'> & { period?: number | string } = {}
  ): Promise<ApiResponse<DashboardStats>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<DashboardStats>(`/admin/dashboard/stats?${searchParams}`)
  }

  async getSalesData(params: QueryParams = {}): Promise<ApiResponse<SalesData[]>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<SalesData[]>(`/admin/dashboard/sales?${searchParams}`)
  }

  async getRecentOrders(params: QueryParams = {}): Promise<ApiResponse<RecentOrder[]>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<RecentOrder[]>(`/admin/dashboard/recent-orders?${searchParams}`)
  }

  async getActivities(params: QueryParams = {}): Promise<ApiResponse<ActivityFeedResponse>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<ActivityFeedResponse>(`/admin/dashboard/activity?${searchParams}`)
  }

  async getTopProducts(
    params: QueryParams & { metric?: 'revenue' | 'quantity' } = {}
  ): Promise<ApiResponse<TopProduct[]>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<TopProduct[]>(`/admin/dashboard/top-products?${searchParams}`)
  }

  async getCustomerAnalytics(
    params: Omit<QueryParams, 'period'> & { period?: number | string } = {}
  ): Promise<ApiResponse<CustomerAnalytics>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<CustomerAnalytics>(`/admin/dashboard/customers?${searchParams}`)
  }

  // Category endpoints
  async getCategories(params: QueryParams = {}): Promise<ApiResponse<Category[]>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<Category[]>(`/admin/categories?${searchParams}`)
  }

  async getCategory(id: string): Promise<ApiResponse<Category>> {
    return this.request<Category>(`/admin/categories/${id}`)
  }

  async createCategory(data: CreateCategoryData): Promise<ApiResponse<Category>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return this.request<Category>('/admin/categories', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  async updateCategory(
    id: string,
    data: Partial<CreateCategoryData>
  ): Promise<ApiResponse<Category>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    formData.append('_method', 'PUT')

    return this.request<Category>(`/admin/categories/${id}`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/categories/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleCategoryStatus(id: string, isActive: boolean): Promise<ApiResponse<Category>> {
    return this.request<Category>(`/admin/categories/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  // Product endpoints
  async getProducts(params: QueryParams = {}): Promise<ApiResponse<Product[]>> {
    const searchParams = new URLSearchParams()

    // Handle each parameter type properly
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })

    return this.request<Product[]>(`/admin/products?${searchParams.toString()}`)
  }

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/admin/products/${id}`)
  }

  async createProduct(data: CreateProductData): Promise<ApiResponse<Product>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`images[${index}]`, file)
            }
          })
        } else if (key === 'additional_features' && Array.isArray(value)) {
          formData.append('additional_features', JSON.stringify(value))
        } else if (key === 'specifications' && Array.isArray(value)) {
          formData.append(key, JSON.stringify(value))
        } else if (key === 'dimensions' && typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return this.request<Product>('/admin/products', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  async updateProduct(id: string, data: Partial<CreateProductData>): Promise<ApiResponse<Product>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'images' && Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`images[${index}]`, file)
            }
          })
        } else if (key === 'additional_features' && Array.isArray(value)) {
          formData.append('additional_features', JSON.stringify(value))
        } else if (key === 'specifications' && Array.isArray(value)) {
          formData.append(key, JSON.stringify(value))
        } else if (key === 'dimensions' && typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    formData.append('_method', 'PUT')

    return this.request<Product>(`/admin/products/${id}`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/products/${id}`, {
      method: 'DELETE',
    })
  }

  async checkSlugAvailability(
    slug: string,
    productId?: string
  ): Promise<ApiResponse<{ available: boolean }>> {
    const params = productId ? `?product_id=${productId}` : ''
    return this.request<{ available: boolean }>(`/admin/products/check-slug/${slug}${params}`)
  }

  async toggleProductPublish(id: string, isActive: boolean): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/admin/products/${id}/publish`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  async toggleProductFeatured(id: string, isFeatured: boolean): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/admin/products/${id}/featured`, {
      method: 'PATCH',
      body: JSON.stringify({ is_featured: isFeatured }),
    })
  }

  async getRelatedProducts(id: string, limit = 8): Promise<ApiResponse<Product[]>> {
    return this.request<Product[]>(`/products/${id}/related?limit=${limit}`)
  }

  async syncProductImages(
    id: string,
    images: { image_url: string; alt_text?: string; sort_order?: number; is_primary?: boolean }[]
  ): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/admin/products/${id}/images`, {
      method: 'POST',
      body: JSON.stringify({ images }),
    })
  }

  // Product image management endpoints
  async uploadProductImage(
    productId: string,
    file: File,
    isPrimary?: boolean,
    altText?: string
  ): Promise<ApiResponse<ProductImage>> {
    const formData = new FormData()
    formData.append('image', file)
    if (isPrimary !== undefined) formData.append('is_primary', isPrimary ? '1' : '0')
    if (altText) formData.append('alt_text', altText)

    return this.request<ProductImage>(`/admin/products/${productId}/images/upload`, {
      method: 'POST',
      body: formData,
    })
  }

  async deleteProductImage(productId: string, imageId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    })
  }

  async updateProductImage(
    productId: string,
    imageId: string,
    data: Partial<ProductImage>
  ): Promise<ApiResponse<ProductImage>> {
    return this.request<ProductImage>(`/admin/products/${productId}/images/${imageId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async setPrimaryProductImage(
    productId: string,
    imageId: string
  ): Promise<ApiResponse<ProductImage>> {
    return this.request<ProductImage>(`/admin/products/${productId}/images/${imageId}/primary`, {
      method: 'PATCH',
    })
  }

  async getProductVariants(productId: string): Promise<ApiResponse<ProductVariant[]>> {
    return this.request<ProductVariant[]>(`/admin/products/${productId}/variants`)
  }

  async createProductVariant(
    productId: string,
    data: CreateProductVariantData
  ): Promise<ApiResponse<ProductVariant>> {
    return this.request<ProductVariant>(`/admin/products/${productId}/variants`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateProductVariant(
    productId: string,
    variantId: string,
    data: Partial<CreateProductVariantData>
  ): Promise<ApiResponse<ProductVariant>> {
    return this.request<ProductVariant>(`/admin/products/${productId}/variants/${variantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteProductVariant(productId: string, variantId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/products/${productId}/variants/${variantId}`, {
      method: 'DELETE',
    })
  }

  async toggleProductVariantStatus(
    productId: string,
    variantId: string,
    isActive: boolean
  ): Promise<ApiResponse<ProductVariant>> {
    return this.request<ProductVariant>(
      `/admin/products/${productId}/variants/${variantId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive }),
      }
    )
  }

  async setDefaultProductVariant(
    productId: string,
    variantId: string
  ): Promise<ApiResponse<ProductVariant>> {
    return this.request<ProductVariant>(
      `/admin/products/${productId}/variants/${variantId}/default`,
      {
        method: 'PATCH',
      }
    )
  }

  async uploadProductVariantImage(
    productId: string,
    variantId: string,
    file: File,
    isPrimary?: boolean,
    altText?: string
  ): Promise<ApiResponse<ProductImage>> {
    const formData = new FormData()
    formData.append('image', file)
    if (isPrimary !== undefined) formData.append('is_primary', isPrimary ? '1' : '0')
    if (altText) formData.append('alt_text', altText)

    return this.request<ProductImage>(
      `/admin/products/${productId}/variants/${variantId}/images/upload`,
      {
        method: 'POST',
        body: formData,
        headers: {},
      }
    )
  }

  async deleteProductVariantImage(
    productId: string,
    variantId: string,
    imageId: string
  ): Promise<ApiResponse<void>> {
    return this.request<void>(
      `/admin/products/${productId}/variants/${variantId}/images/${imageId}`,
      {
        method: 'DELETE',
      }
    )
  }

  async setPrimaryProductVariantImage(
    productId: string,
    variantId: string,
    imageId: string
  ): Promise<ApiResponse<ProductImage>> {
    return this.request<ProductImage>(
      `/admin/products/${productId}/variants/${variantId}/images/${imageId}/primary`,
      {
        method: 'PATCH',
      }
    )
  }

  // Bulk operations for products
  async bulkUpdateProducts(data: {
    product_ids: string[]
    action:
      | 'activate'
      | 'deactivate'
      | 'delete'
      | 'update_price'
      | 'update_stock'
      | 'update_category'
    data?: Record<string, unknown>
  }): Promise<ApiResponse<{ success: number; errors: string[] }>> {
    return this.request<{ success: number; errors: string[] }>('/admin/products/bulk-update', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Bulk operations for users
  async bulkUpdateUsers(data: {
    ids: string[]
    action: 'update_role' | 'delete' | 'verify_email' | 'unverify_email'
    data?: Record<string, unknown>
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/admin/users/bulk-update', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async bulkUpdateUserRoles(data: {
    user_ids: string[]
    role: 'user' | 'viewer' | 'editor' | 'admin' | 'super_admin'
    reason?: string
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/admin/users/bulk-role-update', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Bulk operations for orders
  async bulkUpdateOrders(data: {
    ids: string[]
    action: 'update_status' | 'mark_paid' | 'cancel' | 'delete'
    data?: Record<string, unknown>
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/admin/orders/bulk-update', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async bulkProcessShipping(data: {
    order_ids: string[]
    carrier: string
    service: string
    shipping_cost?: number
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/admin/orders/process-bulk-shipping', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Bulk operations for categories
  async bulkUpdateCategories(data: {
    category_ids: string[]
    action: 'activate' | 'deactivate' | 'delete' | 'move_parent' | 'update_order' | 'update_seo'
    data?: Record<string, unknown>
  }): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/admin/categories/bulk-update', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Order endpoints
  async getOrders(params: QueryParams = {}): Promise<ApiResponse<Order[]>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<Order[]>(`/admin/orders?${searchParams}`)
  }

  async getOrder(id: string): Promise<ApiResponse<Order>> {
    return this.request<Order>(`/admin/orders/${id}`)
  }

  async updateOrder(
    id: string,
    data: {
      shipping_address?: Record<string, unknown>
      shipping_method?: string
      payment_method?: string
      notes?: string
      append_notes?: boolean
      estimated_delivery?: string
    }
  ): Promise<ApiResponse<Order>> {
    return this.request<Order>(`/admin/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async updateOrderStatus(id: string, data: UpdateOrderStatusData): Promise<ApiResponse<Order>> {
    return this.request<Order>(`/admin/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async cancelOrder(id: string): Promise<ApiResponse<Order>> {
    return this.updateOrderStatus(id, { status: 'cancelled' })
  }

  async getOrderStats(
    params: Omit<QueryParams, 'period'> & { period?: string | number } = {}
  ): Promise<ApiResponse<OrderStats>> {
    const searchParams = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<OrderStats>(`/admin/dashboard/order-stats?${searchParams}`)
  }

  async adminUploadPaymentProof(paymentId: string, file: File): Promise<ApiResponse<Order>> {
    const formData = new FormData()
    formData.append('proof_image', file)

    return this.request<Order>(`/admin/payments/${paymentId}/upload-proof`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  // Users endpoints
  async getUsers(params: QueryParams = {}): Promise<ApiResponse<User[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<User[]>(`/admin/users${q}`)
  }

  async createUser(data: CreateUserRequest): Promise<ApiResponse<User>> {
    return this.request<User>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateUser(id: string, data: Partial<User>): Promise<ApiResponse<User>> {
    return this.request<User>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/users/${id}`, {
      method: 'DELETE',
    })
  }

  // Vouchers endpoints
  async getVouchers(params: QueryParams = {}): Promise<ApiResponse<Voucher[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<Voucher[]>(`/admin/vouchers${q}`)
  }

  async getVoucher(id: string): Promise<ApiResponse<Voucher>> {
    return this.request<Voucher>(`/admin/vouchers/${id}`)
  }

  async createVoucher(data: VoucherFormData): Promise<ApiResponse<Voucher>> {
    return this.request<Voucher>('/admin/vouchers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateVoucher(id: string, data: Partial<VoucherFormData>): Promise<ApiResponse<Voucher>> {
    return this.request<Voucher>(`/admin/vouchers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteVoucher(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/vouchers/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleVoucherStatus(id: string): Promise<ApiResponse<Voucher>> {
    return this.request<Voucher>(`/admin/vouchers/${id}/toggle-status`, {
      method: 'PATCH',
    })
  }

  // Client endpoints
  async getClients(params: QueryParams = {}): Promise<ApiResponse<Client[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<Client[]>(`/admin/clients${q}`)
  }

  async getClient(id: string): Promise<ApiResponse<Client>> {
    return this.request<Client>(`/admin/clients/${id}`)
  }

  async createClient(data: ClientFormData): Promise<ApiResponse<Client>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return this.request<Client>('/admin/clients', {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async updateClient(id: string, data: Partial<ClientFormData>): Promise<ApiResponse<Client>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    formData.append('_method', 'PUT')

    return this.request<Client>(`/admin/clients/${id}`, {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async deleteClient(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/clients/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleClientStatus(id: string): Promise<ApiResponse<Client>> {
    return this.request<Client>(`/admin/clients/${id}/toggle-status`, {
      method: 'PATCH',
    })
  }

  async getVoucherUsage(id: string): Promise<
    ApiResponse<{
      voucher: Voucher
      statistics: VoucherUsageStatistics
      recent_usages: VoucherUsageStatistics['recent_usages']
    }>
  > {
    return this.request<{
      voucher: Voucher
      statistics: VoucherUsageStatistics
      recent_usages: VoucherUsageStatistics['recent_usages']
    }>(`/admin/vouchers/${id}/usage`)
  }
  // Review endpoints
  async getReviews(params: QueryParams = {}): Promise<ApiResponse<Review[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<Review[]>(`/admin/reviews${q}`)
  }

  async getReview(id: string): Promise<ApiResponse<Review>> {
    return this.request<Review>(`/admin/reviews/${id}`)
  }

  async createReview(data: ReviewFormData): Promise<ApiResponse<Review>> {
    return this.request<Review>('/admin/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateReview(id: string, data: Partial<ReviewFormData>): Promise<ApiResponse<Review>> {
    return this.request<Review>(`/admin/reviews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteReview(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/reviews/${id}`, {
      method: 'DELETE',
    })
  }

  // Testimonial endpoints (Manual)
  async getTestimonials(params: QueryParams = {}): Promise<ApiResponse<Testimonial[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<Testimonial[]>(`/admin/testimonials${q}`)
  }

  async getTestimonial(id: string): Promise<ApiResponse<Testimonial>> {
    return this.request<Testimonial>(`/admin/testimonials/${id}`)
  }

  async createTestimonial(data: TestimonialFormData): Promise<ApiResponse<Testimonial>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return this.request<Testimonial>('/admin/testimonials', {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async updateTestimonial(
    id: string,
    data: Partial<TestimonialFormData>
  ): Promise<ApiResponse<Testimonial>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else {
          formData.append(key, String(value))
        }
      }
    })

    formData.append('_method', 'PUT')

    return this.request<Testimonial>(`/admin/testimonials/${id}`, {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async deleteTestimonial(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/testimonials/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleTestimonialStatus(id: string): Promise<ApiResponse<Testimonial>> {
    return this.request<Testimonial>(`/admin/testimonials/${id}/toggle-status`, {
      method: 'PATCH',
    })
  }

  // Settings endpoints
  async getSettings(): Promise<
    ApiResponse<
      Array<{
        id: string
        key: string
        value: unknown
        type: string
        description: string
        updated_at: string
      }>
    >
  > {
    return this.request<
      Array<{
        id: string
        key: string
        value: unknown
        type: string
        description: string
        updated_at: string
      }>
    >('/admin/settings')
  }

  async updateSetting(
    key: string,
    value: unknown
  ): Promise<
    ApiResponse<{
      key: string
      value: unknown
      type: string
      description: string
    }>
  > {
    return this.request<{
      key: string
      value: unknown
      type: string
      description: string
    }>(`/admin/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    })
  }

  // Static Pages endpoints
  async getStaticPages(params: QueryParams = {}): Promise<ApiResponse<StaticPage[]>> {
    const q = this.buildQuery(params as Record<string, unknown>)
    return this.request<StaticPage[]>(`/admin/static-pages${q}`)
  }

  async getStaticPage(id: string): Promise<ApiResponse<StaticPage>> {
    return this.request<StaticPage>(`/admin/static-pages/${id}`)
  }

  async createStaticPage(data: StaticPageFormData): Promise<ApiResponse<StaticPage>> {
    return this.request<StaticPage>('/admin/static-pages', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateStaticPage(
    id: string,
    data: Partial<StaticPageFormData>
  ): Promise<ApiResponse<StaticPage>> {
    return this.request<StaticPage>(`/admin/static-pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteStaticPage(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/static-pages/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleStaticPageStatus(id: string): Promise<ApiResponse<StaticPage>> {
    return this.request<StaticPage>(`/admin/static-pages/${id}/toggle-status`, {
      method: 'PATCH',
    })
  }

  // Bank Account endpoints
  async getBankAccounts(): Promise<ApiResponse<BankAccount[]>> {
    return this.request<BankAccount[]>('/admin/bank-accounts')
  }

  async getBankAccount(id: string): Promise<ApiResponse<BankAccount>> {
    return this.request<BankAccount>(`/admin/bank-accounts/${id}`)
  }

  async createBankAccount(data: BankAccountFormData): Promise<ApiResponse<BankAccount>> {
    return this.request<BankAccount>('/admin/bank-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateBankAccount(
    id: string,
    data: Partial<BankAccountFormData>
  ): Promise<ApiResponse<BankAccount>> {
    return this.request<BankAccount>(`/admin/bank-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteBankAccount(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/bank-accounts/${id}`, {
      method: 'DELETE',
    })
  }

  // Hero Slides endpoints
  async getHeroSlides(): Promise<ApiResponse<HeroSlide[]>> {
    return this.request<HeroSlide[]>('/admin/hero-slides')
  }

  async getHeroSlide(id: string): Promise<ApiResponse<HeroSlide>> {
    return this.request<HeroSlide>(`/admin/hero-slides/${id}`)
  }

  async createHeroSlide(data: HeroSlideFormData): Promise<ApiResponse<HeroSlide>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    return this.request<HeroSlide>('/admin/hero-slides', {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async updateHeroSlide(
    id: string,
    data: Partial<HeroSlideFormData>
  ): Promise<ApiResponse<HeroSlide>> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0')
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    formData.append('_method', 'PUT')

    return this.request<HeroSlide>(`/admin/hero-slides/${id}`, {
      method: 'POST',
      body: formData,
      headers: {},
    })
  }

  async deleteHeroSlide(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/hero-slides/${id}`, {
      method: 'DELETE',
    })
  }

  async toggleHeroSlideStatus(id: string): Promise<ApiResponse<HeroSlide>> {
    return this.request<HeroSlide>(`/admin/hero-slides/${id}/toggle-status`, {
      method: 'PATCH',
    })
  }

  async reorderHeroSlides(ids: string[]): Promise<ApiResponse<void>> {
    return this.request<void>('/admin/hero-slides/reorder', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
  }
}

// Import static page types used in this file
type StaticPage = {
  id: string
  title: string
  slug: string
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  content: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

type StaticPageFormData = {
  title: string
  slug?: string
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  content: string
  is_active?: boolean
  sort_order?: number
}

export const apiClient = new ApiClient()
export default apiClient
